import GlassCard from '@/components/GlassCard';
import PageTitle from '@/components/PageTitle';
import ThemeWrapper from '@/components/ThemeWrapper';
import { Heading } from '@/components/Typography';
import AppLayout from '@/layouts/AppLayout';
import VideoPlayer from '@/pages/Courses/ClassDetail/Partials/VideoPlayer';
import { usePage } from '@inertiajs/react';
import { Mic } from 'lucide-react';
import React from 'react';
import { useTranslation } from 'react-i18next';

interface ShadowingSection {
    id: number;
    title: string;
    icon_url: string | null;
    color: string | null;
    course_slug: string;
}

interface IntroVideo {
    video_url: string;
    vimeo_url: string;
}

interface Props {
    sections?: ShadowingSection[];
    intro_video?: IntroVideo | null;
}

const ShadowingSeriesContent: React.FC = () => {
    const { t } = useTranslation();
    const { props } = usePage<Props>();
    const { sections = [], intro_video } = props;

    const isEmpty = !intro_video && sections.length === 0;

    return (
        <AppLayout>
            <PageTitle title={t('home.shadowingSeries')} />
            <div className="mx-auto max-w-6xl space-y-8">
                {isEmpty ? (
                    <GlassCard className="p-10 text-center">
                        <Mic className="mx-auto mb-3 h-10 w-10 text-gray-400" />
                        <p className="text-gray-600 dark:text-neutral-300">
                            {t('home.shadowingSeriesEmpty')}
                        </p>
                    </GlassCard>
                ) : (
                    <>
                        {intro_video ? (
                            <div className="space-y-3">
                                <Heading as="h2">Introduction</Heading>
                                <div className="overflow-hidden rounded-xl">
                                    <VideoPlayer
                                        videoUrl={intro_video.video_url}
                                        vimeoUrl={intro_video.vimeo_url}
                                    />
                                </div>
                            </div>
                        ) : null}

                        {sections.length > 0 ? (
                            <div className="space-y-4">
                                <Heading as="h2">
                                    {t('home.chooseYourLevels')}
                                </Heading>
                                <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
                                    {sections.map((section) => (
                                        <GlassCard
                                            key={section.id}
                                            as="link"
                                            href={`/courses/${section.course_slug}/sections/${section.id}/`}
                                            className="group overflow-hidden"
                                        >
                                            <div className="bg-theme-primary flex items-center justify-center py-5 transition-opacity duration-200 group-hover:opacity-90">
                                                <span className="text-2xl font-bold text-white">
                                                    {section.title}
                                                </span>
                                            </div>
                                        </GlassCard>
                                    ))}
                                </div>
                            </div>
                        ) : null}
                    </>
                )}
            </div>
        </AppLayout>
    );
};

const ShadowingSeries: React.FC<Props> = () => (
    <ThemeWrapper>
        <ShadowingSeriesContent />
    </ThemeWrapper>
);

export default ShadowingSeries;
